import type { AppDispatch } from 'app/store';
import { ahuBoardFormsApi } from 'app/services/ahuBoardFormsApi';
import type { IAhuBoardSection } from 'models';
import { beginCanvasRedrawBatch } from '../three/canvasRedrawScheduler';

/**
 * Phase 6e1 — the §19.2 section.model ABORT+MERGE save queue (race protection
 * for fast typing): a pending PATCH for a section is aborted and its changed
 * fields merged into the new one (latest-wins per key). §26.1 pins this as
 * "serialized per-section mutation queue replacing the abort+merge: keep
 * latest-wins merge of pending patches".
 *
 * Output fields are stripped before save (§19.2: aw_length/aw_height/aw_width
 * never sent — §26.3 rule 1).
 */

const OUTPUT_FIELDS = ['aw_length', 'aw_height', 'aw_width'];

interface PendingEntry {
  merged: Record<string, unknown>;
  abort: (() => void) | null;
}

const pendingBySection = new Map<number, PendingEntry>();

export interface SectionPatchArgs {
  dispatch: AppDispatch;
  sectionId: number;
  tunnelId: number;
  changes: Record<string, unknown>;
  /** Fires with the server row + the merged change-set that produced it. */
  onSuccess?: (section: IAhuBoardSection, changes: Record<string, unknown>) => void;
}

export function queueSectionPatch({
  dispatch,
  sectionId,
  tunnelId,
  changes,
  onSuccess,
}: SectionPatchArgs): void {
  const cleaned: Record<string, unknown> = {};
  Object.keys(changes).forEach((key) => {
    if (!OUTPUT_FIELDS.includes(key)) cleaned[key] = changes[key];
  });
  if (!Object.keys(cleaned).length) return;

  const prev = pendingBySection.get(sectionId);
  if (prev) {
    prev.abort?.();
    Object.assign(prev.merged, cleaned);
  }
  const merged = prev ? prev.merged : { ...cleaned };
  const entry: PendingEntry = { merged, abort: null };
  pendingBySection.set(sectionId, entry);

  const action = dispatch(
    ahuBoardFormsApi.endpoints.patchAhuSection.initiate({
      id: sectionId,
      tunnelId,
      data: { ...merged },
    })
  );
  entry.abort = () => action.abort();

  /**
   * One canvas pass per section edit (see three/canvasRedrawScheduler). A
   * section PATCH writes the RTK Query cache four times — the optimistic
   * apply, the server echo, and `recalcTunnelLength` riding both — and
   * `onSuccess` then fires the ported `change:tunnel:dimensions` pass on top,
   * so a single length edit rebuilt the geometry three times over. Legacy's
   * canvas is data-blind and draws only on that last event; holding the canvas
   * across the request collapses ours to the same one draw. It also means a
   * burst of typing redraws once when the burst ends rather than per
   * keystroke, which is how the abort+merge above already treats the saves.
   */
  const releaseCanvas = beginCanvasRedrawBatch();

  action
    .unwrap()
    .then((section) => {
      // Only clear if WE are still the live entry (not superseded).
      if (pendingBySection.get(sectionId) === entry) pendingBySection.delete(sectionId);
      try {
        onSuccess?.(section, { ...merged });
      } finally {
        releaseCanvas();
      }
    })
    .catch(() => {
      // Aborted (superseded) or failed — legacy keeps no rollback; a
      // superseded entry's merged map already lives in the successor.
      if (pendingBySection.get(sectionId) === entry) pendingBySection.delete(sectionId);
      releaseCanvas();
    });
}

/**
 * True while this section has a PATCH on the wire. The syncdata listener uses
 * it to let the user's own save win over a websocket echo (useAhuBoardSyncdata).
 */
export function hasPendingSectionPatch(sectionId: number): boolean {
  return pendingBySection.has(sectionId);
}

/** Board unmount — drop pending state (fresh-boot semantics). */
export function resetSectionPatchQueue(): void {
  pendingBySection.forEach((entry) => entry.abort?.());
  pendingBySection.clear();
}
